import { Box, Button, Icon, TextField } from "@material-ui/core";
import MaterialTable, { MTableToolbar } from "material-table";
import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  APP_URLS,
  PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  ROLES,
} from "../../../../utilities/constants";
import { getUserInfo } from "../../../../utilities/storage";
import { getHeadOrgById } from "../../HeadOrgsActions";
import { getHeadOrgsRequest } from "../../HeadOrgsApiActions";
import { useStyles } from "./styles";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const getColumns = (classes) => [
  {
    field: "ownerName",
    title: "Organization Name",
    filtering: false,
    cellStyle: { width: "25%" },
    render: ({ id, ownerName }) => (
      <Box
        className={classes.profileContainer}
        component={Link}
        to={`${APP_URLS.HEADORGS}/${id}`}
      >
        {ownerName}
      </Box>
    ),
  },
  {
    field: "ownerEmail",
    title: "Organization Email",
    filtering: false,
    cellStyle: { width: "25%" },
  },
  {
    field: "ownerPhone",
    title: "Organization Contact",
    filtering: false,
    cellStyle: { width: "25%" },
  },
  {
    field: "ownerAddress",
    title: "Address",
    filtering: false,
    cellStyle: { width: "auto" },
  },
  {
    field: "clinicDescription",
    title: "Organization Description",
    filtering: false,
    cellStyle: { width: "auto" },
  },
];

const FilterComponent = React.memo(({ localFilters, onFilterChange }) => (
  <Box
    display="flex"
    gap={3}
    sx={{
      padding: "0 16px",
      flexWrap: "wrap",
      "& .MuiFormControl-root, & .MuiTextField-root": { marginRight: 150 },
    }}
  >
    <TextField
      label="Search Name"
      variant="outlined"
      size="small"
      value={localFilters.ownerName}
      onChange={onFilterChange("ownerName")}
    />
    <TextField
      label="Search Email"
      variant="outlined"
      size="small"
      value={localFilters.ownerEmail}
      onChange={onFilterChange("ownerEmail")}
    />
    <TextField
      label="Search Phone"
      variant="outlined"
      size="small"
      value={localFilters.ownerPhone}
      onChange={onFilterChange("ownerPhone")}
    />
  </Box>
));

const HeadOrgList = ({ history, getHeadOrgs, headOrgs }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const role = getUserInfo().Roles[0].name;

  const [localFilters, setLocalFilters] = useState({
    ownerName: "",
    ownerEmail: "",
    ownerPhone: "",
  });

  const [paginationParams, setPaginationParams] = useState({
    page: 0,
    rowsPerPage: PAGE_SIZE,
    searchData: { ownerName: "", ownerEmail: "", ownerPhone: "" },
    isHideLoader: false,
  });

  const debouncedFilters = useDebounce(localFilters, 400);

  const handleFilterChange = (field) => (e) => {
    setLocalFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  useEffect(() => {
    setPaginationParams((prev) => ({
      ...prev,
      searchData: debouncedFilters,
      page: 0,
    }));
  }, [debouncedFilters]);

  useEffect(() => {
    let isMounted = true;

    getHeadOrgs(paginationParams)
      .then(() => {
        if (isMounted) {
          dispatch(getHeadOrgById(null));
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error(err);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [paginationParams, getHeadOrgs, dispatch]);

  const onAdd = () => history.push(APP_URLS.HEADORGS_ADD);

  return (
    <Box className={classes.outerContainer}>
      <Box mt={1}>
        {role === ROLES.ROLE_SUPER_ADMIN && (
          <Box className={classes.addButtonContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={onAdd}
              startIcon={<Icon>add</Icon>}
              disableElevation
              classes={{ root: classes.addButton }}
            >
              Add
            </Button>
          </Box>
        )}

        <FilterComponent
          localFilters={localFilters}
          onFilterChange={handleFilterChange}
        />

        <MaterialTable
          title="Head Organizations"
          columns={getColumns(classes)}
          data={headOrgs.rows || []}
          style={{ marginTop: "20px" }}
          options={{
            search: false,
            filtering: false,
            sorting: false,
            draggable: false,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            pageSize: paginationParams.rowsPerPage,
            emptyRowsWhenPaging: false,
          }}
          totalCount={headOrgs.count}
          page={paginationParams.page}
          onChangeRowsPerPage={(pageSize) =>
            setPaginationParams((prev) => ({ ...prev, rowsPerPage: pageSize }))
          }
          onChangePage={(page) =>
            setPaginationParams((prev) => ({ ...prev, page }))
          }
          components={{
            Toolbar: (props) => <MTableToolbar {...props} />,
          }}
        />
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  headOrgs: state.headOrgs.headOrgs,
});

const mapDispatchToProps = (dispatch) => ({
  getHeadOrgs: (data) =>
    dispatch(getHeadOrgsRequest(data)).then(
      (res) => Promise.resolve(res),
      (error) => Promise.reject(error)
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(HeadOrgList);
